import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { RefreshCw, EyeOff, AlertTriangle } from 'lucide-react';

import {
  Feed,
  Article,
  FeedStatus,
  FeedRetryState,
  SortOrder,
  FeedLastRefresh,
} from './types';
import { getCookie, setCookie, deleteCookie } from './utils/cookies';
import { decodeBase64DataUrl } from './utils/encoding';
import { parseRSSXML } from './utils/rssParser';
import { cacheArticles, restoreCachedArticles } from './utils/articleCache';
import {
  prefetchArticle,
  evictCachedParse,
  clearParseCache,
} from './services/articleParserService';
import {
  analyzeClickbaitWithAI,
  cancelBackgroundRetry,
  applyHeuristicAnalysis,
} from './services/aiService';
import { useCookieConsent, useTimer, useAI, useTranslation } from './hooks';
import { Theme } from './i18n/translations';
import {
  Header,
  Footer,
  ControlBar,
  OnboardingPicker,
  TimeLimitOverlay,
  SettingsPanel,
  ArticleCard,
} from './components';
import { deriveSystemState } from './utils/systemState';
// AboutPanel only mounts when the user clicks About — keep it out of the
// initial bundle.
const AboutPanel = lazy(() =>
  import('./components/AboutPanel').then(m => ({ default: m.AboutPanel })),
);

const REFRESH_COOLDOWN_MS = 30 * 60 * 1000;
const MAX_FEEDS = 10;
// Per-feed timeout — was 60s. The server already races 4 sources internally
// and retries once with stale-while-error fallback; 15s is plenty for the
// client's hop and avoids one slow feed holding up the whole UI.
const FEED_TIMEOUT_MS = 15000;
const MAX_RETRY_DURATION_MS = 15 * 60 * 1000;
// Cap on stored "read" article ids so the localStorage entry doesn't grow
// without bound across days.
const MAX_READ_ARTICLES = 1000;

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

// Replaces the old "from today" rule with a rolling 24h window so an
// article published at 11pm doesn't vanish at midnight while the user is
// still reading it.
const isArticleRecent = (article: Article): boolean => {
  const diff = Date.now() - article.pubDate.getTime();
  return diff >= 0 && diff < TWENTY_FOUR_HOURS_MS;
};

const getRetryDelay = (attempt: number): number => 30 * Math.pow(2, attempt) * 1000;

const RealFrontPage: React.FC = () => {
  // State (feeds first so we can pass to useTimer)
  const [feeds, setFeeds] = useState<Feed[]>([]);

  // Hooks
  const { cookieConsent } = useCookieConsent();
  const { timeSpentToday, timeLimitReached, dailyTimeLimit, setDailyTimeLimit, formatTimeRemaining } = useTimer(cookieConsent, feeds.length > 0);
  const { aiAvailable, aiChecking } = useAI();
  const { language, setLanguage, t } = useTranslation(cookieConsent);

  // State
  const [articles, setArticles] = useState<Article[]>([]);
  const [readArticles, setReadArticles] = useState<Set<string>>(new Set());
  const [newFeedUrl, setNewFeedUrl] = useState<string>('');
  const [newFeedName, setNewFeedName] = useState<string>('');
  const [deClickbaitMode, setDeClickbaitMode] = useState<boolean>(true);
  // Cleanse mode default: ON. Existing users with the cookie set keep their
  // choice (the cookie loader below overrides this initial value).
  const [cleanMode, setCleanMode] = useState<boolean>(true);
  const [hideClickbait, setHideClickbait] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAbout, setShowAbout] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [feedStatus, setFeedStatus] = useState<FeedStatus>({});
  const [feedLastRefresh, setFeedLastRefresh] = useState<FeedLastRefresh>({});
  const [articlesPerPage, setArticlesPerPage] = useState<number>(15);
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  const [theme, setTheme] = useState<Theme>('system');
  const [sortOrder, setSortOrder] = useState<SortOrder>('relevance');
  const [articleParsingEnabled, setArticleParsingEnabled] = useState<boolean>(true);
  const [feedsErrored, setFeedsErrored] = useState<boolean>(false);

  // Refs hold the live values used by setTimeout/abort callbacks so they
  // never read stale closures from when they were scheduled.
  const feedsRef = useRef<Feed[]>([]);
  const articlesPerPageRef = useRef<number>(15);
  const readArticlesRef = useRef<Set<string>>(new Set());
  const aiAvailableRef = useRef<boolean>(false);
  const articleParsingEnabledRef = useRef<boolean>(true);
  const sortOrderRef = useRef<SortOrder>('relevance');

  // Wave coordination — re-entrancy guard + abort handle. fetchAllFeeds is
  // the only entry point that sets these; nested helpers read them.
  const inFlightWaveRef = useRef<boolean>(false);
  const waveAbortRef = useRef<AbortController | null>(null);
  // Snapshot of articles visible at wave start, so we can keep showing them
  // alongside newly-arriving feed articles until the wave finishes.
  const waveCachedSnapshotRef = useRef<Article[]>([]);
  // Accumulator of articles fetched in the current wave, in the order
  // feeds resolve. Used both for progressive paint and final cache write.
  const waveNewArticlesRef = useRef<Article[]>([]);

  // Initial-hydration sentinel: replaces the old feedsLoadedRef.
  // Set true exactly once after the first cookie/cache pass, so subsequent
  // dependency flips don't repeat the startup logic — but we expose
  // resetHydration() for cookieConsent transitions that legitimately
  // need to re-run the boot path.
  const hydrationCompleteRef = useRef<boolean>(false);

  const retryStateRef = useRef<Map<string, FeedRetryState>>(new Map());

  // Sync refs with state so callbacks always see live values.
  useEffect(() => { feedsRef.current = feeds; }, [feeds]);
  useEffect(() => { articlesPerPageRef.current = articlesPerPage; }, [articlesPerPage]);
  useEffect(() => { readArticlesRef.current = readArticles; }, [readArticles]);
  useEffect(() => { aiAvailableRef.current = aiAvailable; }, [aiAvailable]);
  useEffect(() => { articleParsingEnabledRef.current = articleParsingEnabled; }, [articleParsingEnabled]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

  // Cross-midnight detection: if the user leaves the tab open through
  // midnight and comes back, the cooldown timer should reset so they can
  // refresh immediately. Also useful after bfcache restore.
  useEffect(() => {
    const checkCrossMidnight = () => {
      if (lastRefreshTime === null) return;
      const refreshDate = new Date(lastRefreshTime).toDateString();
      const today = new Date().toDateString();
      if (refreshDate !== today) {
        deleteCookie('last_refresh_time');
        setLastRefreshTime(null);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkCrossMidnight();
    };
    const handlePageShow = () => checkCrossMidnight();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [lastRefreshTime]);

  // Load saved settings from cookies + read_articles from localStorage
  // (with one-time migration from the old cookie storage).
  useEffect(() => {
    if (cookieConsent !== true) return;

    const savedArticlesPerPage = getCookie('articles_per_page');
    const savedDeClickbaitMode = getCookie('de_clickbait_mode');
    const savedCleanMode = getCookie('clean_mode');
    const savedHideClickbait = getCookie('hide_clickbait');
    const savedLastRefresh = getCookie('last_refresh_time');
    const savedTheme = getCookie('theme');
    const today = new Date().toDateString();

    if (savedLastRefresh) {
      const savedTime = parseInt(savedLastRefresh, 10);
      const savedDate = new Date(savedTime).toDateString();
      if (savedDate !== today) {
        deleteCookie('last_refresh_time');
      } else {
        setLastRefreshTime(savedTime);
      }
    }

    if (savedArticlesPerPage) {
      setArticlesPerPage(parseInt(savedArticlesPerPage, 10));
    }

    if (savedDeClickbaitMode !== null) setDeClickbaitMode(savedDeClickbaitMode === 'true');
    if (savedCleanMode !== null) setCleanMode(savedCleanMode === 'true');
    if (savedHideClickbait !== null) setHideClickbait(savedHideClickbait === 'true');
    if (savedTheme && (savedTheme === 'system' || savedTheme === 'light' || savedTheme === 'dark')) {
      setTheme(savedTheme);
    }

    const savedSortOrder = getCookie('sort_order');
    if (savedSortOrder && (savedSortOrder === 'time' || savedSortOrder === 'relevance')) {
      setSortOrder(savedSortOrder);
    }

    const savedArticleParsing = getCookie('article_parsing_enabled');
    if (savedArticleParsing !== null) {
      setArticleParsingEnabled(savedArticleParsing === 'true');
    }

    try {
      const savedFeedLastRefresh = localStorage.getItem('feed_last_refresh');
      if (savedFeedLastRefresh) {
        setFeedLastRefresh(JSON.parse(savedFeedLastRefresh));
      }
    } catch {
      // localStorage unavailable
    }

    // read_articles: migrate from cookie if present, otherwise localStorage.
    // Cookie storage was hitting the ~4 KB per-cookie limit, silently
    // dropping the value and resurrecting "read" articles on next refresh.
    try {
      const lsDate = localStorage.getItem('read_articles_date');
      const lsRead = localStorage.getItem('read_articles');
      if (lsDate !== today) {
        localStorage.removeItem('read_articles');
        localStorage.setItem('read_articles_date', today);
      } else if (lsRead) {
        setReadArticles(new Set(JSON.parse(lsRead)));
      }

      // One-time cookie → localStorage migration.
      const legacyCookie = getCookie('read_articles');
      const legacyDate = getCookie('read_articles_date');
      if (legacyCookie) {
        if (legacyDate === today && !lsRead) {
          try {
            const ids = JSON.parse(legacyCookie) as string[];
            setReadArticles(new Set(ids));
            localStorage.setItem('read_articles', JSON.stringify(ids));
          } catch {
            // malformed cookie — drop it
          }
        }
        deleteCookie('read_articles');
      }
      if (legacyDate) {
        deleteCookie('read_articles_date');
      }
    } catch {
      // localStorage unavailable
    }
  }, [cookieConsent]);

  // Load and fetch feeds after consent + AI check both settle.
  useEffect(() => {
    if (cookieConsent !== true || aiChecking) return;
    if (hydrationCompleteRef.current) return;

    const savedFeeds = getCookie('rss_feeds');
    if (!savedFeeds) {
      hydrationCompleteRef.current = true;
      return;
    }

    let parsedFeeds: Feed[] = [];
    try {
      parsedFeeds = JSON.parse(savedFeeds);
    } catch {
      deleteCookie('rss_feeds');
      hydrationCompleteRef.current = true;
      return;
    }

    setFeeds(parsedFeeds);
    if (parsedFeeds.length === 0) {
      hydrationCompleteRef.current = true;
      return;
    }

    const cachedArticles = restoreCachedArticles();
    const savedLastRefresh = getCookie('last_refresh_time');
    const lastRefresh = savedLastRefresh ? parseInt(savedLastRefresh, 10) : null;
    const today = new Date().toDateString();
    const cacheIsFromToday = lastRefresh !== null && new Date(lastRefresh).toDateString() === today;
    const cacheValid = cacheIsFromToday && (Date.now() - (lastRefresh || 0)) < REFRESH_COOLDOWN_MS;

    const restoreFiltered = () => {
      if (cachedArticles.length === 0) return;
      const filtered = cachedArticles
        .filter(a => isArticleRecent(a) && !readArticlesRef.current.has(a.id))
        .sort((a, b) => b.pubDate.getTime() - a.pubDate.getTime());
      setArticles(filtered);
    };

    if (timeLimitReached) {
      restoreFiltered();
      hydrationCompleteRef.current = true;
      return;
    }

    if (cacheValid) {
      restoreFiltered();
      if (lastRefresh) setLastRefreshTime(lastRefresh);
      hydrationCompleteRef.current = true;
      return;
    }

    hydrationCompleteRef.current = true;
    fetchAllFeeds(parsedFeeds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieConsent, aiChecking, timeLimitReached]);

  // Save feeds to cookie (only after hydration to avoid clobbering on first mount)
  useEffect(() => {
    if (cookieConsent !== true) return;
    if (!hydrationCompleteRef.current) return;

    if (feeds.length > 0) {
      setCookie('rss_feeds', JSON.stringify(feeds));
    } else {
      deleteCookie('rss_feeds');
    }
  }, [feeds, cookieConsent]);

  // Save read articles to localStorage (cap to MAX_READ_ARTICLES, newest first).
  useEffect(() => {
    if (cookieConsent !== true) return;
    try {
      const arr = Array.from(readArticles);
      const trimmed = arr.length > MAX_READ_ARTICLES ? arr.slice(-MAX_READ_ARTICLES) : arr;
      localStorage.setItem('read_articles', JSON.stringify(trimmed));
      localStorage.setItem('read_articles_date', new Date().toDateString());
    } catch {
      // quota or unavailable — fall through
    }
  }, [readArticles, cookieConsent]);

  // Save other settings to cookies
  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('articles_per_page', articlesPerPage.toString());
  }, [articlesPerPage, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('de_clickbait_mode', deClickbaitMode.toString());
  }, [deClickbaitMode, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('clean_mode', cleanMode.toString());
  }, [cleanMode, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('hide_clickbait', hideClickbait.toString());
  }, [hideClickbait, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('theme', theme);
  }, [theme, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('sort_order', sortOrder);
  }, [sortOrder, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('article_parsing_enabled', articleParsingEnabled.toString());
  }, [articleParsingEnabled, cookieConsent]);

  // Apply dark mode class to document and update theme-color meta tag
  useEffect(() => {
    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      const themeColor = isDark ? '#111827' : '#ffffff';
      let metaThemeColor = document.querySelector('meta[name="theme-color"]:not([media])');
      if (!metaThemeColor) {
        document.querySelectorAll('meta[name="theme-color"]').forEach(el => el.remove());
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', themeColor);
    };

    if (theme === 'dark') {
      applyTheme(true);
    } else if (theme === 'light') {
      applyTheme(false);
    } else {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mediaQuery.matches);
      const handleChange = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  // Cleanup retries + abort wave on unmount
  useEffect(() => {
    const currentRetries = retryStateRef.current;
    const currentWave = waveAbortRef;
    return () => {
      currentRetries.forEach(state => {
        if (state.timeoutId) clearTimeout(state.timeoutId);
      });
      currentWave.current?.abort();
    };
  }, []);

  // ---- Fetch helpers ----

  const fetchWithTimeout = async (
    url: string,
    timeoutMs: number,
    parentSignal?: AbortSignal,
  ): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const onParentAbort = () => controller.abort();
    parentSignal?.addEventListener('abort', onParentAbort);

    try {
      return await fetch(url, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
      parentSignal?.removeEventListener('abort', onParentAbort);
    }
  };

  const fetchSingleFeed = async (
    feed: Feed,
    limit: number,
    parentSignal?: AbortSignal,
  ): Promise<Article[]> => {
    const proxyUrl = `/api/rss?url=${encodeURIComponent(feed.url)}`;

    try {
      const response = await fetchWithTimeout(proxyUrl, FEED_TIMEOUT_MS, parentSignal);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      let xmlText = await response.text();
      if (xmlText.startsWith('data:')) {
        xmlText = decodeBase64DataUrl(xmlText);
      }
      return parseRSSXML(xmlText, feed.name, limit);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error(parentSignal?.aborted ? 'Aborted' : 'Timeout');
      }
      throw err;
    }
  };

  // scheduleRetry is intentionally NOT useCallback'd — it reads from refs,
  // so a new reference per render is harmless. Recursive self-reference
  // works because each scheduled timeout closes over the current render's
  // copy and they're functionally identical.
  const scheduleRetry = (feed: Feed, attempt: number, startTime: number = Date.now()): void => {
    const elapsed = Date.now() - startTime;
    if (elapsed >= MAX_RETRY_DURATION_MS) {
      setFeedStatus(prev => ({ ...prev, [feed.name]: `✗ Failed (gave up after 15 min)` }));
      retryStateRef.current.delete(feed.url);
      return;
    }

    const delay = getRetryDelay(attempt);
    const delaySeconds = delay / 1000;
    setFeedStatus(prev => ({ ...prev, [feed.name]: `✗ Failed, retrying in ${delaySeconds}s...` }));

    const timeoutId = setTimeout(async () => {
      setFeedStatus(prev => ({ ...prev, [feed.name]: `Retrying (attempt ${attempt + 2})...` }));

      // Read fresh values from refs — the wave that scheduled this retry may
      // have completed minutes ago with a different feed list / settings.
      const currentFeeds = feedsRef.current;
      const currentArticlesPerPage = articlesPerPageRef.current;
      const currentReadArticles = readArticlesRef.current;
      const currentAiAvailable = aiAvailableRef.current;

      const articlesPerSource = Math.ceil(
        (currentArticlesPerPage * 3) / Math.max(currentFeeds.length, 1),
      );

      try {
        const items = await fetchSingleFeed(feed, articlesPerSource);
        retryStateRef.current.delete(feed.url);
        setFeedStatus(prev => ({
          ...prev,
          [feed.name]: `✓ ${items.length} articles (today)`,
        }));

        if (items.length > 0) {
          const { articles: analyzedItems } = await analyzeClickbaitWithAI(
            items,
            currentAiAvailable,
            10,
          );
          setArticles(prev => {
            const newArticles = analyzedItems.filter(
              item =>
                !prev.some(a => a.id === item.id) &&
                !currentReadArticles.has(item.id),
            );
            return [...prev, ...newArticles].sort(
              (a, b) => b.pubDate.getTime() - a.pubDate.getTime(),
            );
          });
        }
      } catch {
        scheduleRetry(feed, attempt + 1, startTime);
      }
    }, delay);

    retryStateRef.current.set(feed.url, { feed, attempt, timeoutId });
  };

  const cancelAllRetries = (): void => {
    retryStateRef.current.forEach(state => {
      if (state.timeoutId) clearTimeout(state.timeoutId);
    });
    retryStateRef.current.clear();
    cancelBackgroundRetry();
  };

  const reloadSingleFeed = async (feed: Feed): Promise<void> => {
    const retryState = retryStateRef.current.get(feed.url);
    if (retryState?.timeoutId) {
      clearTimeout(retryState.timeoutId);
      retryStateRef.current.delete(feed.url);
    }

    setFeedStatus(prev => ({ ...prev, [feed.name]: 'Loading...' }));

    const articlesPerSource = Math.ceil(
      (articlesPerPageRef.current * 3) / Math.max(feedsRef.current.length, 1),
    );

    try {
      const items = await fetchSingleFeed(feed, articlesPerSource);
      const now = Date.now();

      setFeedStatus(prev => ({
        ...prev,
        [feed.name]: `✓ ${items.length} articles (today)`,
      }));

      setFeedLastRefresh(prev => {
        const updated = { ...prev, [feed.name]: now };
        try {
          localStorage.setItem('feed_last_refresh', JSON.stringify(updated));
        } catch {
          // ignore
        }
        return updated;
      });

      if (items.length > 0) {
        const { articles: analyzedItems } = await analyzeClickbaitWithAI(
          items,
          aiAvailableRef.current,
          10,
        );
        setArticles(prev => {
          const newArticles = analyzedItems.filter(
            item =>
              !prev.some(a => a.id === item.id) &&
              !readArticlesRef.current.has(item.id),
          );
          const merged = [...prev, ...newArticles].sort(
            (a, b) => b.pubDate.getTime() - a.pubDate.getTime(),
          );
          // Persist merged result so a browser refresh after adding a source
          // doesn't blow the new feed's articles away (cacheValid path needs
          // both the cache AND last_refresh_time to be populated).
          cacheArticles(merged);
          return merged;
        });

        setLastRefreshTime(now);
        if (cookieConsent === true) {
          setCookie('last_refresh_time', now.toString(), 1);
        }
      }

      setFeedsErrored(false); // at least one feed works now
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setFeedStatus(prev => ({ ...prev, [feed.name]: `✗ ${errorMessage}` }));
    }
  };

  // Build the visible-articles view used both during a wave (cached + new)
  // and at wave end (new only). Keeping the dedup/filter/sort logic in one
  // place keeps progressive paint and finalization aligned.
  const buildVisibleArticles = useCallback(
    (sources: Article[][]): Article[] => {
      const seen = new Set<string>();
      const result: Article[] = [];
      for (const source of sources) {
        for (const a of source) {
          if (seen.has(a.id) || readArticlesRef.current.has(a.id)) continue;
          seen.add(a.id);
          result.push(a);
        }
      }
      return result.sort((a, b) => b.pubDate.getTime() - a.pubDate.getTime());
    },
    [],
  );

  // Background AI enrichment. Runs after a wave's heuristic results are
  // already painted, then upgrades them in place. Aborts cleanly if the
  // wave is superseded.
  const runAiEnrichment = async (
    heuristicArticles: Article[],
    signal: AbortSignal,
  ): Promise<void> => {
    if (heuristicArticles.length === 0) return;
    if (signal.aborted) return;

    // Reuse prior AI analysis from same-day cache, so the AI only re-runs
    // on articles we haven't seen before. We only treat an article as
    // "already analyzed" when the cached version was AI-derived — heuristic
    // fallbacks (from a previous failed AI run) carry aiAnalyzed=false/undef
    // and should be re-tried.
    const cachedAnalysisMap = new Map<string, Article>();
    try {
      const cachedArticlesStr = localStorage.getItem('cached_articles');
      const cachedDate = localStorage.getItem('cached_articles_date');
      const today = new Date().toDateString();
      if (cachedArticlesStr && cachedDate === today) {
        const cached: Article[] = JSON.parse(cachedArticlesStr);
        cached.forEach(a => {
          if (a.aiAnalyzed === true) cachedAnalysisMap.set(a.id, a);
        });
      }
    } catch {
      // ignore
    }

    const preserved: Article[] = [];
    const toAnalyze: Article[] = [];
    for (const a of heuristicArticles) {
      const cached = cachedAnalysisMap.get(a.id);
      if (cached && cached.aiAnalyzed === true) {
        preserved.push({
          ...a,
          isClickbait: cached.isClickbait,
          cleanedTitle: cached.cleanedTitle,
          cleanedDescription: cached.cleanedDescription,
          clickbaitCategory: cached.clickbaitCategory,
          clickbaitExplanation: cached.clickbaitExplanation,
          relevanceScore: cached.relevanceScore,
          aiAnalyzed: true,
        });
      } else {
        toAnalyze.push(a);
      }
    }

    setFeedStatus(prev => ({ ...prev, analysis: 'Analyzing articles...' }));

    const applyEnrichedToState = (enriched: Article[]) => {
      if (signal.aborted) return;
      const byId = new Map(enriched.map(a => [a.id, a]));
      setArticles(prev => prev.map(a => byId.get(a.id) || a));
      cacheArticles(enriched);
    };

    if (toAnalyze.length === 0) {
      setFeedStatus(prev => ({ ...prev, analysis: '✓ Using cached analysis' }));
      applyEnrichedToState(preserved);
      return;
    }

    const onBackgroundRetrySuccess = (retryArticles: Article[]) => {
      if (signal.aborted) return;
      setFeedStatus(prev => ({ ...prev, analysis: '✓ Analysis complete' }));
      applyEnrichedToState([...preserved, ...retryArticles]);
    };

    const result = await analyzeClickbaitWithAI(
      toAnalyze,
      aiAvailableRef.current,
      articlesPerPageRef.current * 3,
      onBackgroundRetrySuccess,
    );

    if (signal.aborted) return;

    if (result.usedHeuristicFallback) {
      setFeedStatus(prev => ({
        ...prev,
        analysis: '⏳ Analysis pending (retrying in background)',
      }));
    } else {
      setFeedStatus(prev => ({ ...prev, analysis: '✓ Analysis complete' }));
    }

    applyEnrichedToState([...preserved, ...result.articles]);
  };

  const fetchAllFeeds = async (feedList?: Feed[]): Promise<void> => {
    const feedsToFetch = feedList || feedsRef.current;
    if (feedsToFetch.length === 0) {
      setLoading(false);
      return;
    }

    // Cancel any prior wave (re-entrancy guard). Fast double-clicks
    // previously spawned racing pipelines that overwrote each other.
    waveAbortRef.current?.abort();
    cancelAllRetries();

    const abortController = new AbortController();
    waveAbortRef.current = abortController;
    const signal = abortController.signal;
    inFlightWaveRef.current = true;

    if (cookieConsent) {
      const refreshTime = Date.now();
      setLastRefreshTime(refreshTime);
      setCookie('last_refresh_time', refreshTime.toString(), 1);
    }

    setLoading(true);
    setFeedsErrored(false);

    const initialStatus: FeedStatus = {};
    feedsToFetch.forEach(feed => {
      initialStatus[feed.name] = 'Loading...';
    });
    setFeedStatus(initialStatus);

    const articlesPerSource = Math.ceil((articlesPerPageRef.current * 3) / feedsToFetch.length);

    // Snapshot what's currently visible — keep it on screen during the wave.
    waveCachedSnapshotRef.current = articles;
    waveNewArticlesRef.current = [];

    let successCount = 0;
    let failureCount = 0;
    const refreshTimes: FeedLastRefresh = { ...feedLastRefresh };

    // Each feed paints into state as soon as it resolves — no more "wait
    // for the slowest of 10 feeds" before any article is visible.
    const feedPromises = feedsToFetch.map(async feed => {
      try {
        const items = await fetchSingleFeed(feed, articlesPerSource, signal);
        if (signal.aborted) return;

        successCount++;
        refreshTimes[feed.name] = Date.now();

        const heuristic = applyHeuristicAnalysis(items);
        for (const a of heuristic) {
          if (!waveNewArticlesRef.current.some(x => x.id === a.id)) {
            waveNewArticlesRef.current.push(a);
          }
        }

        setFeedStatus(prev => ({
          ...prev,
          [feed.name]: `✓ ${items.length} articles (today)`,
        }));

        // Progressive paint: merge fresh + cached snapshot.
        setArticles(
          buildVisibleArticles([
            waveNewArticlesRef.current,
            waveCachedSnapshotRef.current,
          ]),
        );
      } catch (err) {
        if (signal.aborted) return;
        failureCount++;
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setFeedStatus(prev => ({ ...prev, [feed.name]: `✗ ${errorMessage}` }));
        scheduleRetry(feed, 0);
      }
    });

    await Promise.allSettled(feedPromises);

    if (signal.aborted) {
      inFlightWaveRef.current = false;
      return;
    }

    setFeedLastRefresh(refreshTimes);
    try {
      localStorage.setItem('feed_last_refresh', JSON.stringify(refreshTimes));
    } catch {
      // ignore
    }

    if (successCount === 0 && failureCount > 0) {
      setFeedsErrored(true);
    }

    // Replace state with just the freshly-fetched articles (drop cached
    // snapshot) so feeds that disappeared from the user's list don't linger.
    const finalNewArticles = [...waveNewArticlesRef.current];
    setArticles(buildVisibleArticles([finalNewArticles]));

    // Cache heuristic version immediately so a tab close before AI finishes
    // still leaves something useful for next session.
    if (finalNewArticles.length > 0) {
      cacheArticles(finalNewArticles);
    }

    setLoading(false);
    inFlightWaveRef.current = false;

    // AI enrichment runs in background — articles are already visible.
    if (finalNewArticles.length > 0) {
      runAiEnrichment(finalNewArticles, signal).catch(() => {
        // Silent — we've already painted heuristic results.
      });
    }
  };

  // ---- User actions ----

  const addFeed = (): void => {
    if (!newFeedUrl.trim() || !newFeedName.trim()) {
      setError(t.enterBothFields);
      return;
    }
    if (feeds.length >= MAX_FEEDS) {
      setError(t.maxFeedsReached);
      return;
    }

    const trimmedUrl = newFeedUrl.trim();
    const trimmedName = newFeedName.trim();

    if (feeds.some(f => f.url.toLowerCase() === trimmedUrl.toLowerCase())) {
      setError(t.duplicateFeedUrl);
      return;
    }
    if (feeds.some(f => f.name.toLowerCase() === trimmedName.toLowerCase())) {
      setError(t.duplicateFeedName);
      return;
    }

    setError('');
    const newFeed: Feed = { url: trimmedUrl, name: trimmedName };
    const updatedFeeds = [...feeds, newFeed];
    setFeeds(updatedFeeds);
    setNewFeedUrl('');
    setNewFeedName('');

    if (cookieConsent === true) {
      setCookie('rss_feeds', JSON.stringify(updatedFeeds));
    }

    reloadSingleFeed(newFeed);
  };

  // Add a batch of feeds in one shot. Used by the empty-state onboarding
  // picker, which lets the user select several sources before committing.
  // Doing them one-at-a-time would race on `feeds` state (each setFeeds
  // would close over the same empty array). Silently dedupes against the
  // current list and against itself, and respects MAX_FEEDS.
  const addFeedsBatch = (toAdd: Feed[]): void => {
    if (toAdd.length === 0) return;
    const seenUrls = new Set(feeds.map(f => f.url.toLowerCase()));
    const seenNames = new Set(feeds.map(f => f.name.toLowerCase()));
    const accepted: Feed[] = [];
    for (const feed of toAdd) {
      if (feeds.length + accepted.length >= MAX_FEEDS) break;
      const url = feed.url.toLowerCase();
      const name = feed.name.toLowerCase();
      if (seenUrls.has(url) || seenNames.has(name)) continue;
      seenUrls.add(url);
      seenNames.add(name);
      accepted.push(feed);
    }
    if (accepted.length === 0) return;
    const updatedFeeds = [...feeds, ...accepted];
    setFeeds(updatedFeeds);
    if (cookieConsent === true) {
      setCookie('rss_feeds', JSON.stringify(updatedFeeds));
    }
    for (const feed of accepted) reloadSingleFeed(feed);
  };

  const removeFeed = (url: string): void => {
    const feedToRemove = feeds.find(f => f.url === url);
    const feedNameToRemove = feedToRemove?.name;

    const updatedFeeds = feeds.filter(f => f.url !== url);
    setFeeds(updatedFeeds);

    if (cookieConsent === true) {
      if (updatedFeeds.length > 0) {
        setCookie('rss_feeds', JSON.stringify(updatedFeeds));
      } else {
        deleteCookie('rss_feeds');
      }
    }

    if (feedNameToRemove) {
      setArticles(prevArticles => prevArticles.filter(a => a.feedName !== feedNameToRemove));
      try {
        const cachedArticles = localStorage.getItem('cached_articles');
        if (cachedArticles) {
          const parsed = JSON.parse(cachedArticles);
          const filtered = parsed.filter((a: Article) => a.feedName !== feedNameToRemove);
          localStorage.setItem('cached_articles', JSON.stringify(filtered));
        }
      } catch {
        // ignore
      }
    }

    if (feedNameToRemove) {
      setFeedStatus(prev => {
        const updated = { ...prev };
        delete updated[feedNameToRemove];
        return updated;
      });
    }
  };

  const markAsRead = (articleId: string): void => {
    const updated = new Set(readArticles);
    updated.add(articleId);
    setReadArticles(updated);
    // Drop the parsed-article entry — the user has dismissed it, no point
    // keeping the parsed body warm.
    const article = articles.find(a => a.id === articleId);
    if (article) evictCachedParse(article.link);
    setArticles(articles.filter(a => a.id !== articleId));
  };

  const markAllAsRead = (): void => {
    if (articles.length === 0) return;
    const updated = new Set(readArticles);
    for (const a of articles) {
      updated.add(a.id);
      evictCachedParse(a.link);
    }
    setReadArticles(updated);
    setArticles([]);
  };

  const exportData = (): void => {
    const data = { feeds };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'real-front-page-feeds.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.feeds) {
          const limitedFeeds = data.feeds.slice(0, MAX_FEEDS);
          setFeeds(limitedFeeds);
          fetchAllFeeds(limitedFeeds);
        }
      } catch {
        setError(t.importError);
      }
    };
    reader.readAsText(file);
  };

  const toggleSettings = (): void => {
    setShowSettings(prev => !prev);
    setShowAbout(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleAbout = (): void => {
    setShowAbout(prev => !prev);
    setShowSettings(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---- Memoized derived data ----

  const sortArticles = useCallback(
    (list: Article[], order: SortOrder): Article[] => {
      if (order === 'relevance') {
        return [...list].sort((a, b) => {
          const scoreA = a.relevanceScore ?? 5;
          const scoreB = b.relevanceScore ?? 5;
          if (scoreB !== scoreA) return scoreB - scoreA;
          return b.pubDate.getTime() - a.pubDate.getTime();
        });
      }
      return [...list].sort((a, b) => b.pubDate.getTime() - a.pubDate.getTime());
    },
    [],
  );

  const distributeArticles = useCallback(
    (allArticles: Article[], totalLimit: number): Article[] => {
      if (allArticles.length === 0) return [];

      const bySource: Map<string, Article[]> = new Map();
      for (const article of allArticles) {
        const existing = bySource.get(article.feedName) || [];
        existing.push(article);
        bySource.set(article.feedName, existing);
      }

      const sourceCount = bySource.size;
      if (sourceCount === 0) return [];

      const basePerSource = Math.floor(totalLimit / sourceCount);
      let remaining = totalLimit;

      const result: Article[] = [];
      const sourcesWithExtra: string[] = [];

      for (const [sourceName, sourceArticles] of bySource) {
        const sorted = sortArticles(sourceArticles, sortOrder);
        const toTake = Math.min(basePerSource, sorted.length);
        result.push(...sorted.slice(0, toTake));
        remaining -= toTake;
        if (sorted.length > basePerSource) {
          sourcesWithExtra.push(sourceName);
        }
      }

      if (remaining > 0 && sourcesWithExtra.length > 0) {
        for (const sourceName of sourcesWithExtra) {
          if (remaining <= 0) break;
          const sourceArticles = bySource.get(sourceName)!;
          const sorted = sortArticles(sourceArticles, sortOrder);
          const alreadyTaken = Math.min(basePerSource, sorted.length);
          const available = sorted.slice(alreadyTaken);
          for (const article of available) {
            if (remaining <= 0) break;
            result.push(article);
            remaining--;
          }
        }
      }

      return sortArticles(result, sortOrder);
    },
    [sortArticles, sortOrder],
  );

  const visibleArticles = useMemo(() => {
    const filtered = hideClickbait ? articles.filter(a => !a.isClickbait) : articles;
    return distributeArticles(filtered, articlesPerPage);
  }, [articles, hideClickbait, articlesPerPage, distributeArticles]);

  // Idle-time prefetch of the top few visible articles. Picks "top" by
  // relevance score so users hitting the highest-signal items get an
  // instant modal. Strictly capped (3) to keep speculative work cheap —
  // hover/touch prefetch in ArticleCard covers the rest.
  useEffect(() => {
    if (!articleParsingEnabled) return;
    if (visibleArticles.length === 0) return;

    const top = [...visibleArticles]
      .sort((a, b) => (b.relevanceScore ?? 5) - (a.relevanceScore ?? 5))
      .slice(0, 3);

    const controller = new AbortController();
    const run = () => {
      if (controller.signal.aborted) return;
      for (const a of top) prefetchArticle(a.link, controller.signal);
    };

    // requestIdleCallback isn't on Safari/iOS — fall back to a short
    // setTimeout so the prefetch yields to the initial paint either way.
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof ric === 'function') {
      idleId = ric(run, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(run, 800);
    }

    return () => {
      controller.abort();
      const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (idleId !== undefined && typeof cic === 'function') cic(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [visibleArticles, articleParsingEnabled]);

  // When a fresh wave is about to overwrite the article list, the cache's
  // entries for the old (soon-to-disappear) articles are dead weight.
  // Clearing on loading=true keeps the cache tightly aligned with what's
  // currently on screen.
  useEffect(() => {
    if (loading && articles.length === 0) clearParseCache();
  }, [loading, articles.length]);

  // Settings-panel stats. Merges currently-visible articles with the cache
  // so reading or dismissing items doesn't reset the day's tally — the
  // cache is rewritten on each fetch and survives mark-as-read.
  const feedStats = useMemo(() => {
    const cached = restoreCachedArticles();
    const merged = new Map<string, Article>();
    for (const a of cached) merged.set(a.id, a);
    for (const a of articles) merged.set(a.id, a);
    let flagged = 0;
    let cleansed = 0;
    for (const a of merged.values()) {
      if (a.isClickbait) {
        flagged++;
        if (a.cleanedTitle && a.cleanedTitle !== a.title) cleansed++;
      }
    }
    return { loaded: merged.size, flagged, cleansed };
  }, [articles]);

  // ---- Render ----

  const showLoadingBox = loading && articles.length === 0;
  const showErrorBanner = feedsErrored && !loading;

  // Per-feed failures surfaced from feedStatus. Successful entries start
  // with "✓"; failures start with "✗" or are mid-retry. We only show the
  // partial-failure banner when SOME feeds failed (the all-failed banner
  // above already covers the everything-down case).
  const failedFeedNames = useMemo(() => {
    return feeds
      .map(f => f.name)
      .filter(name => {
        const s = feedStatus[name];
        return !!s && (s.startsWith('✗') || /failed|error/i.test(s));
      });
  }, [feeds, feedStatus]);
  const showFeedHealthBanner = !loading && !showErrorBanner && failedFeedNames.length > 0;

  const systemState = useMemo(
    () => deriveSystemState(
      {
        aiChecking,
        aiAvailable,
        loading,
        feedsErrored,
        failedFeedCount: failedFeedNames.length,
        analysisStatus: feedStatus.analysis,
        hasFeeds: feeds.length > 0,
      },
      t,
    ),
    [aiChecking, aiAvailable, loading, feedsErrored, failedFeedNames.length, feedStatus.analysis, feeds.length, t],
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-black focus:text-white dark:focus:bg-white dark:focus:text-black focus:font-semibold"
      >
        Skip to main content
      </a>

      {timeLimitReached && (
        <TimeLimitOverlay dailyTimeLimit={dailyTimeLimit} t={t} />
      )}

      <Header
        timeRemaining={formatTimeRemaining()}
        timeSpentToday={timeSpentToday}
        dailyTimeLimit={dailyTimeLimit}
        language={language}
        t={t}
      />

      {showSettings && (
        <SettingsPanel
          feeds={feeds}
          feedStatus={feedStatus}
          feedLastRefresh={feedLastRefresh}
          newFeedUrl={newFeedUrl}
          setNewFeedUrl={setNewFeedUrl}
          newFeedName={newFeedName}
          setNewFeedName={setNewFeedName}
          onAddFeed={addFeed}
          onAddFeeds={addFeedsBatch}
          onRemoveFeed={removeFeed}
          onReloadFeed={reloadSingleFeed}
          error={error}
          dailyTimeLimit={dailyTimeLimit}
          onTimeLimitChange={setDailyTimeLimit}
          articlesPerPage={articlesPerPage}
          onArticlesPerPageChange={setArticlesPerPage}
          language={language}
          onLanguageChange={setLanguage}
          theme={theme}
          onThemeChange={setTheme}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          articleParsingEnabled={articleParsingEnabled}
          onArticleParsingChange={setArticleParsingEnabled}
          t={t}
          onExport={exportData}
          onImport={importData}
          feedStats={feedStats}
        />
      )}

      {showAbout && (
        <Suspense fallback={<div className="px-4 py-8 text-gray-500 dark:text-gray-400">Loading…</div>}>
          <AboutPanel t={t} />
        </Suspense>
      )}

      <main id="main-content" className="px-4 sm:px-6 py-4 sm:py-8" role="main" aria-label="News articles">
        {showLoadingBox && (
          <div className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 max-w-2xl mx-auto p-6" role="status" aria-live="polite">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="animate-spin w-6 h-6 text-black dark:text-white flex-shrink-0" aria-hidden="true" />
              <p className="text-gray-700 dark:text-gray-300 font-semibold">{t.loadingArticles.replace('{count}', feeds.length.toString())}</p>
            </div>

            <div className="space-y-1.5 text-sm">
              {feeds.map(feed => {
                const status = feedStatus[feed.name];
                const isLoading = !status || status === 'Loading...';
                const isSuccess = status?.startsWith('✓');
                const isError = status?.startsWith('✗');

                return (
                  <div key={feed.url} className="flex items-center gap-2">
                    {isLoading && (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300 rounded-full animate-spin flex-shrink-0" />
                    )}
                    {isSuccess && (
                      <span className="text-green-600 dark:text-green-400 flex-shrink-0">✓</span>
                    )}
                    {isError && (
                      <span className="text-red-600 dark:text-red-400 flex-shrink-0">✗</span>
                    )}
                    <span className={`truncate ${isLoading ? 'text-gray-500 dark:text-gray-400' : isSuccess ? 'text-gray-700 dark:text-gray-300' : 'text-red-600 dark:text-red-400'}`}>
                      {feed.name}
                    </span>
                    {status && !isLoading && (
                      <span className="text-gray-400 dark:text-gray-500 text-xs ml-auto flex-shrink-0">
                        {status.replace('✓ ', '').replace('✗ ', '')}
                      </span>
                    )}
                  </div>
                );
              })}

              {feedStatus.analysis && (
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                  {feedStatus.analysis.startsWith('✓') ? (
                    <span className="text-green-600 dark:text-green-400 flex-shrink-0">✓</span>
                  ) : (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300 rounded-full animate-spin flex-shrink-0" />
                  )}
                  <span className="text-gray-600 dark:text-gray-400">{feedStatus.analysis.replace('✓ ', '')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {showErrorBanner && (
          <div
            role="alert"
            className="max-w-2xl mx-auto mb-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm text-red-800 dark:text-red-200">{t.feedsAllFailed}</p>
              <button
                onClick={() => fetchAllFeeds()}
                disabled={loading}
                className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
                {t.retry}
              </button>
            </div>
          </div>
        )}

        {showFeedHealthBanner && (
          <div
            role="status"
            className="max-w-2xl mx-auto mb-4 bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-700 p-3 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-900 dark:text-amber-100">
                {(failedFeedNames.length === 1 ? t.feedsSomeFailedOne : t.feedsSomeFailedMany)
                  .replace('{count}', String(failedFeedNames.length))
                  .replace('{names}', failedFeedNames.join(', '))}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {failedFeedNames.length === 1 && (() => {
                  const feed = feeds.find(f => f.name === failedFeedNames[0]);
                  if (!feed) return null;
                  return (
                    <button
                      onClick={() => reloadSingleFeed(feed)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700"
                    >
                      <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                      {t.retry}
                    </button>
                  );
                })()}
                <button
                  onClick={toggleSettings}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold border-2 border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
                >
                  {t.manageFeedsAction}
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && feeds.length === 0 && (
          <OnboardingPicker
            language={language}
            onAddFeeds={addFeedsBatch}
            onOpenSettings={toggleSettings}
            onLearnMore={toggleAbout}
            t={t}
          />
        )}

        {!loading && feeds.length > 0 && articles.length === 0 && !showErrorBanner && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 max-w-2xl mx-auto">
            <p className="text-gray-600 dark:text-gray-400">{t.noArticlesToday}</p>
          </div>
        )}

        {!loading && visibleArticles.length === 0 && hideClickbait && articles.length > 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 max-w-2xl mx-auto">
            <EyeOff className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">{t.allArticlesHidden}</p>
          </div>
        )}

        {visibleArticles.length > 0 && (
          <div
            className="grid gap-3 sm:gap-4"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
            }}
          >
            {visibleArticles.map(article => (
              <ArticleCard
                key={article.id}
                article={article}
                size="medium"
                deClickbaitMode={deClickbaitMode}
                cleanMode={cleanMode}
                onMarkAsRead={() => markAsRead(article.id)}
                t={t}
                articleParsingEnabled={articleParsingEnabled}
              />
            ))}
          </div>
        )}
      </main>

      <Footer t={t} />

      <ControlBar
        deClickbaitMode={deClickbaitMode}
        setDeClickbaitMode={setDeClickbaitMode}
        cleanMode={cleanMode}
        setCleanMode={setCleanMode}
        hideClickbait={hideClickbait}
        setHideClickbait={setHideClickbait}
        showSettings={showSettings}
        onToggleSettings={toggleSettings}
        showAbout={showAbout}
        onToggleAbout={toggleAbout}
        onRefresh={() => fetchAllFeeds()}
        onMarkAllRead={markAllAsRead}
        hasUnread={visibleArticles.length > 0}
        loading={loading}
        hasFeeds={feeds.length > 0}
        lastRefreshTime={lastRefreshTime}
        refreshCooldownMs={REFRESH_COOLDOWN_MS}
        systemState={systemState}
        t={t}
      />

    </div>
  );
};

export default RealFrontPage;
